import EstimationsService from '@/services/api/estimations.services'

const state = {
    estimations: [],
    estimationsMeta: { total: 0, per_page: 20, current_page: 1, last_page: 1 },
    filters: {
        author_id: null,
        status: null,
        per_page: 20,
        page: 1,
        include: null,
    },
    currentEstimation: null,
    currentPortfolio: [],
    stacks: [],
    roles: [],
    isLoading: false,
}

const mutations = {
    SET_ESTIMATIONS(state, payload) {
        state.estimations = payload || []
    },
    SET_ESTIMATIONS_META(state, payload) {
        state.estimationsMeta = {
            total: payload?.total || 0,
            per_page: payload?.per_page || 20,
            current_page: payload?.current_page || 1,
            last_page: payload?.last_page || 1,
        }
    },
    SET_FILTERS(state, payload) {
        state.filters = { ...state.filters, ...(payload || {}) }
    },
    SET_CURRENT(state, payload) {
        state.currentEstimation = payload || null
    },
    SET_CURRENT_PORTFOLIO(state, payload) {
        state.currentPortfolio = payload || []
    },
    SET_STACKS(state, payload) {
        state.stacks = payload || []
    },
    SET_ROLES(state, payload) {
        state.roles = payload || []
    },
    SET_LOADING(state, payload) {
        state.isLoading = !!payload
    },
    REMOVE_ESTIMATION(state, id) {
        state.estimations = state.estimations.filter((e) => e.id !== id)
    },
}

const unwrap = (response) => {
    const root = response && response.data ? response.data : {}
    return root.data !== undefined ? root.data : root
}

const buildQuery = (filters) => {
    const out = {}
    Object.entries(filters || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') out[k] = v
    })
    return out
}

const actions = {
    setFilters({ commit }, payload) {
        commit('SET_FILTERS', payload)
    },

    getEstimations({ commit, state }, params = {}) {
        commit('SET_LOADING', true)
        const merged = buildQuery({ ...state.filters, ...params })
        return new Promise((resolve, reject) => {
            EstimationsService.listEstimations(merged)
                .then((response) => {
                    const root = response?.data || {}
                    const list = Array.isArray(root.data) ? root.data : root.data?.data || []
                    const meta = root.meta || root.data?.meta || {}
                    commit('SET_ESTIMATIONS', list)
                    commit('SET_ESTIMATIONS_META', meta)
                    commit('SET_LOADING', false)
                    return resolve(response)
                })
                .catch((error) => {
                    commit('SET_LOADING', false)
                    return reject(error)
                })
        })
    },

    getEstimation({ commit }, id) {
        commit('SET_LOADING', true)
        return new Promise((resolve, reject) => {
            EstimationsService.getEstimation(id)
                .then((response) => {
                    const data = unwrap(response)
                    const estimation = data?.estimation || data
                    commit('SET_CURRENT', estimation || null)
                    if (data?.portfolio !== undefined) commit('SET_CURRENT_PORTFOLIO', data.portfolio || [])
                    commit('SET_LOADING', false)
                    return resolve(response)
                })
                .catch((error) => {
                    commit('SET_LOADING', false)
                    return reject(error)
                })
        })
    },

    createEstimation({ commit }, payload) {
        commit('SET_LOADING', true)
        return new Promise((resolve, reject) => {
            EstimationsService.createEstimation(payload)
                .then((response) => {
                    const data = unwrap(response)
                    const estimation = data?.estimation || data
                    commit('SET_CURRENT', estimation || null)
                    if (data?.portfolio !== undefined) commit('SET_CURRENT_PORTFOLIO', data.portfolio || [])
                    commit('SET_LOADING', false)
                    return resolve(response)
                })
                .catch((error) => {
                    commit('SET_LOADING', false)
                    return reject(error)
                })
        })
    },

    updateEstimation({ commit }, { id, payload }) {
        return new Promise((resolve, reject) => {
            EstimationsService.updateEstimation(id, payload)
                .then((response) => {
                    const data = unwrap(response)
                    const estimation = data?.estimation || data
                    commit('SET_CURRENT', estimation || null)
                    return resolve(response)
                })
                .catch((error) => reject(error))
        })
    },

    deleteEstimation({ commit }, id) {
        return new Promise((resolve, reject) => {
            EstimationsService.deleteEstimation(id)
                .then((response) => {
                    commit('REMOVE_ESTIMATION', id)
                    return resolve(response)
                })
                .catch((error) => reject(error))
        })
    },

    getStacks({ commit }, params = {}) {
        return new Promise((resolve, reject) => {
            EstimationsService.listStacks(params)
                .then((response) => {
                    const data = unwrap(response)
                    const list = Array.isArray(data) ? data : data?.data || []
                    commit('SET_STACKS', list)
                    return resolve(response)
                })
                .catch((error) => reject(error))
        })
    },
    createStack(_context, payload) {
        return EstimationsService.createStack(payload)
    },
    updateStack(_context, { id, payload }) {
        return EstimationsService.updateStack(id, payload)
    },
    deleteStack(_context, id) {
        return EstimationsService.deleteStack(id)
    },

    createFeature(_context, { estimationId, payload }) {
        return EstimationsService.createFeature(estimationId, payload)
    },
    updateFeatureAction(_context, { estimationId, featureId, payload }) {
        return EstimationsService.updateFeature(estimationId, featureId, payload)
    },
    deleteFeatureAction(_context, { estimationId, featureId }) {
        return EstimationsService.deleteFeature(estimationId, featureId)
    },

    getRoles({ commit }, params = {}) {
        return new Promise((resolve, reject) => {
            EstimationsService.listRoles(params)
                .then((response) => {
                    const data = unwrap(response)
                    const list = Array.isArray(data) ? data : data?.data || []
                    commit('SET_ROLES', list)
                    return resolve(response)
                })
                .catch((error) => reject(error))
        })
    },
    createRole(_context, payload) {
        return EstimationsService.createRole(payload)
    },
    updateRole(_context, { id, payload }) {
        return EstimationsService.updateRole(id, payload)
    },
    deleteRole(_context, id) {
        return EstimationsService.deleteRole(id)
    },
}

const getters = {
    estimations: (state) => state.estimations,
    estimationsMeta: (state) => state.estimationsMeta,
    filters: (state) => state.filters,
    currentEstimation: (state) => state.currentEstimation,
    currentPortfolio: (state) => state.currentPortfolio,
    stacks: (state) => state.stacks,
    roles: (state) => state.roles,
    isLoading: (state) => state.isLoading,
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
